import { useState } from "react";

import { AnularCotizacion } from "@/modules/cotizador/AnularCotizacion";
import { Aviso } from "@/shared/ui/Aviso/Aviso";
import { Boton } from "@/shared/ui/Boton/Boton";
import { pesos } from "@/shared/utilidades/formato";
import { fecha as fechaCorta } from "@/shared/utilidades/formato";

import {
  useDesenlazarFactura,
  useDocumentosDeLaObra,
  useQuitarFacturaDeCotizacion,
  useRenglonesDelDocumento,
} from "./useDocumentosDeLaObra";
import { useTotalesDeLaOrden } from "./useTotalesDeLaOrden";
import { DocumentoAlAlistar } from "./DocumentoAlAlistar";
import { useDocumentosAlAlistar } from "./useDocumentoAlAlistar";
import estilos from "./DocumentosDeLaObra.module.css";

/**
 * TODOS LOS PAPELES DE LA OBRA, EN UNA LISTA.
 *
 * Johan, señalando la columna derecha: «todo me debería salir ahí. Por
 * ejemplo, FEV XXXX, valor. Y que sea expandible según la que yo cliqué».
 *
 * Antes eran tres sitios para la misma pregunta —qué papeles tiene esta obra
 * y qué dice cada uno—: la cotización principal con sus renglones siempre
 * desplegados, los adicionales como una lista de números que no se podían
 * abrir, y las facturas en ninguna parte.
 *
 * Aquí es una fila por documento, cerrada, con lo único que se mira de
 * reojo: qué es, su número y su valor. Se abre el que interesa, y solo
 * entonces se piden sus renglones.
 */

/** Lo de dentro, cuando alguien abre uno. */
function Renglones({
  clase,
  referencia,
}: {
  clase: string;
  referencia: string;
}) {
  const { renglones, cargando } = useRenglonesDelDocumento(clase, referencia);

  if (cargando) return <p className={estilos.cargando}>Abriendo…</p>;

  if (renglones.length === 0) {
    return (
      <p className={estilos.cargando}>
        Este documento no tiene renglones de este lado. Está en World Office.
      </p>
    );
  }

  const base = renglones.reduce((s, r) => s + Number(r.subtotal ?? 0), 0);
  const iva = renglones.reduce(
    (s, r) => s + Number(r.subtotal ?? 0) * Number(r.iva ?? 0),
    0
  );

  return (
    <div className={estilos.dentro}>
      <table className={estilos.tabla}>
        <tbody>
          {renglones.map((r) => (
            <tr key={r.orden}>
              <td className={estilos.cantidad}>
                {r.cantidad ?? "—"} {r.unidad ?? "Und."}
              </td>
              <td className={estilos.concepto}>{r.descripcion}</td>
              <td className={estilos.importe}>{pesos(r.subtotal)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <p className={estilos.sumas}>
        <span>
          Subtotal <b>{pesos(base)}</b>
        </span>
        <span>
          IVA <b>{pesos(iva)}</b>
        </span>
      </p>
    </div>
  );
}

/** El botón para soltar una factura de la obra, con su confirmación. */
function QuitarFactura({
  ordenServicioId,
  documento,
}: {
  ordenServicioId: string;
  documento: { clase: string; referencia: string; numero: string };
}) {
  const desenlazar = useDesenlazarFactura(ordenServicioId);
  const quitarDeCotizacion = useQuitarFacturaDeCotizacion(ordenServicioId);
  const [confirmando, setConfirmando] = useState(false);
  const trabajando = desenlazar.isPending || quitarDeCotizacion.isPending;
  const error = desenlazar.error ?? quitarDeCotizacion.error;
  const esDeLaOrden = documento.clase === "factura";

  function quitar() {
    if (esDeLaOrden) desenlazar.mutate(Number(documento.referencia));
    else quitarDeCotizacion.mutate(documento.referencia);
  }

  return (
    <div className={estilos.quitar}>
      {!confirmando ? (
        <button
          type="button"
          className={estilos.quitarBoton}
          onClick={() => setConfirmando(true)}
        >
          {esDeLaOrden
            ? "Quitar esta factura de la obra"
            : "Quitarle la factura a este adicional"}
        </button>
      ) : (
        <>
          <span className={estilos.quitarTexto}>
            {esDeLaOrden
              ? `Se quita ${documento.numero} de esta obra. La factura sigue existiendo en World Office.`
              : `${
                  documento.numero.split(" → ")[0]
                } queda sin factura, y la app no se la vuelve a amarrar sola. La factura sigue existiendo en World Office.`}
          </span>
          <Boton
            variante="peligro"
            tamano="pequeno"
            onClick={quitar}
            trabajando={trabajando}
          >
            Sí, quitarla
          </Boton>
          <Boton
            variante="secundario"
            tamano="pequeno"
            onClick={() => setConfirmando(false)}
          >
            Cancelar
          </Boton>
        </>
      )}
      {error && <p className={estilos.quitarError}>{error.message}</p>}
    </div>
  );
}

export function DocumentosDeLaObra({
  ordenServicioId,
  onEnlazarCotizacion,
  onEnlazarFactura,
  onArmarAdicional,
  onEnlazarAdicional,
  onVerDocumento,
  puedeEnlazar,
}: {
  ordenServicioId: string;
  onEnlazarCotizacion: () => void;
  onEnlazarFactura: () => void;
  /** Solo cuando la obra admite adicionales (ya tiene principal aprobada). */
  onArmarAdicional?: () => void;
  onEnlazarAdicional?: () => void;
  /** Abre la hoja de la obra entera (consolidado si hay varias). */
  onVerDocumento?: () => void;
  puedeEnlazar: boolean;
}) {
  const { documentos, cargando } = useDocumentosDeLaObra(ordenServicioId);
  const { totales } = useTotalesDeLaOrden(ordenServicioId);
  // 0296: FEV o remisión, por cotización.
  const { data: alAlistar = {} } = useDocumentosAlAlistar(ordenServicioId);
  // Uno abierto a la vez: dos documentos desplegados a la vez ya no se
  // comparan, se buscan.
  const [abierto, setAbierto] = useState<string | null>(null);
  // El adicional que se va a anular (0290).
  const [anulando, setAnulando] = useState<{
    id: string;
    numero: string;
  } | null>(null);

  if (cargando)
    return <p className={estilos.cargando}>Cargando los documentos…</p>;

  // Una cotización viva —ni anulada ni rechazada— es la principal de la obra.
  const tienePrincipal = documentos.some(
    (d) => d.clase === "cotizacion" && !d.anulado && d.estado !== "rechazada"
  );

  return (
    <div className={estilos.zona}>
      {anulando && (
        <AnularCotizacion
          abierto
          cotizacionId={anulando.id}
          numero={anulando.numero}
          onCerrar={() => setAnulando(null)}
        />
      )}
      {documentos.length === 0 ? (
        <Aviso tipo="info">
          Esta obra todavía no tiene ningún documento. Se le puede enlazar una
          cotización o una factura que ya exista en World Office.
        </Aviso>
      ) : (
        <ul className={estilos.lista}>
          {documentos.map((d) => {
            const clave = `${d.clase}:${d.referencia}`;
            const estaAbierto = abierto === clave;

            return (
              <li
                key={clave}
                className={`${estilos.fila} ${
                  d.anulado ? estilos.anulada : ""
                } ${estaAbierto ? estilos.filaAbierta : ""}`}
              >
                <button
                  type="button"
                  className={estilos.cabeza}
                  onClick={() => setAbierto(estaAbierto ? null : clave)}
                  aria-expanded={estaAbierto}
                >
                  <span className={estilos.marca} aria-hidden="true">
                    {estaAbierto ? "▾" : "▸"}
                  </span>

                  <span className={estilos.identidad}>
                    <span className={estilos.numero}>{d.numero}</span>
                    <span className={estilos.tipo}>
                      {d.etiqueta}
                      {d.fecha && ` · ${fechaCorta(d.fecha)}`}
                      {d.estado &&
                        d.clase === "cotizacion" &&
                        ` · ${d.estado.replace(/_/g, " ")}`}
                      {d.anulado && " · anulada"}
                      {/* 0296: lo dice sin abrirla. */}
                      {alAlistar[d.referencia]?.documento === "remision" &&
                        " · va a remisión"}
                    </span>
                  </span>

                  <span className={estilos.valor}>{pesos(d.valor)}</span>
                </button>

                {estaAbierto && (
                  <Renglones clase={d.clase} referencia={d.referencia} />
                )}

                {/* FEV O REMISIÓN (0296): se decide en la cotización y
                    bodega, al alistarla, la vuelve eso. */}
                {estaAbierto &&
                  d.clase === "cotizacion" &&
                  !d.anulado &&
                  d.estado !== "rechazada" &&
                  alAlistar[d.referencia] && (
                    <DocumentoAlAlistar
                      ordenServicioId={ordenServicioId}
                      decision={alAlistar[d.referencia]}
                      puedeCambiar={puedeEnlazar}
                    />
                  )}

                {/* ANULAR UN ADICIONAL (0290): aprobado y sin factura. Para
                    rehacerlo cuando el cliente cambia lo que pide; la
                    principal no se anula desde aquí. */}
                {estaAbierto &&
                  puedeEnlazar &&
                  d.clase === "cotizacion" &&
                  !d.es_principal &&
                  d.estado === "aprobada" && (
                    <div className={estilos.quitar}>
                      <button
                        type="button"
                        className={estilos.quitarBoton}
                        onClick={() =>
                          setAnulando({ id: d.referencia, numero: d.numero })
                        }
                      >
                        Anular este adicional
                      </button>
                    </div>
                  )}

                {/* QUITAR UNA FACTURA MAL PUESTA (0286). Johan: «no hay
                    forma de eliminar el enlazado». Sirve para la que se
                    enlazó a mano a la orden y para la que se amarró sola a
                    un adicional —la de la principal no se toca (Johan: «solo
                    quitar las adicionales»)—. */}
                {estaAbierto &&
                  puedeEnlazar &&
                  (d.clase === "factura" ||
                    (d.estado === "facturada" && !d.es_principal)) && (
                    <QuitarFactura
                      ordenServicioId={ordenServicioId}
                      documento={d}
                    />
                  )}
              </li>
            );
          })}
        </ul>
      )}

      {/* LA SUMA DE LA OBRA, debajo de sus papeles.

          Johan: «¿el documento completo debería sumar todas las
          cotizaciones?». Cada una es un documento distinto en World Office y
          no hay un papel que las junte; lo que faltaba era la cifra. La
          calcula la base (fn_totales_de_la_orden), la misma de la cabecera.

          LAS FACTURAS SÍ SUMAN (0231), sin contar dos veces: la cotización
          que World Office convirtió en factura cuenta una sola vez, como
          factura, y sigue en su fila («FEV 2981 → FEV 14123»). Debajo, cuánto
          va facturado y cuánto falta. */}
      {totales && documentos.length > 0 && (
        <div className={estilos.total}>
          <span className={estilos.totalRotulo}>Total de la obra</span>
          <b className={estilos.totalCifra}>{pesos(totales.aprobado)}</b>
          {(totales.en_propuesta > 0 || totales.facturado > 0) && (
            <span className={estilos.totalDetalle}>
              {totales.en_propuesta > 0 &&
                `+ ${pesos(totales.en_propuesta)} por aprobar`}
              {totales.en_propuesta > 0 && totales.facturado > 0 && " · "}
              {totales.facturado > 0 &&
                `Facturado ${pesos(
                  totales.facturado
                )} · falta por facturar ${pesos(
                  Math.max(0, totales.pendiente_de_cobrar)
                )}`}
            </span>
          )}
        </div>
      )}

      {/* LOS DOS CAMINOS, uno al lado del otro. Una obra se cotiza y se
          factura: antes solo se podía colgar lo primero.

          PERO LA COTIZACIÓN SOLO MIENTRAS NO HAYA UNA. Johan: «¿para qué me
          sale eso cuando ya tengo una? Si no puedo enlazar nada más sino
          desde adicional». Una obra tiene UNA cotización principal; todo lo
          que aparece después va como adicional, que tiene su propio botón
          más abajo. Ofrecer «Enlazar cotización» con la principal ya puesta
          invitaba a colgar una segunda que la base iba a rechazar.

          La factura sí sigue: facturar llega precisamente después. */}
      {(puedeEnlazar || onVerDocumento) && (
        <div className={estilos.acciones}>
          {/* TODOS EN UNA FILA Y TODOS CON FORMA DE BOTÓN. Johan: «¿por qué
              estos tres no están en el mismo renglón? Aparte uno no se
              entiende que es botón» —«Enlazar adicional» iba como texto
              suelto—. Textos cortos y tamaño pequeño, con los nombres que
              usa Johan —«Enlazar COT», «Enlazar FEV»—: con los largos la
              tarjeta los partía en dos renglones. */}
          {puedeEnlazar && (
            <>
              {onArmarAdicional && (
                <Boton
                  variante="secundario"
                  tamano="pequeno"
                  onClick={onArmarAdicional}
                >
                  Armar adicional
                </Boton>
              )}
              {onEnlazarAdicional && (
                <Boton
                  variante="secundario"
                  tamano="pequeno"
                  onClick={onEnlazarAdicional}
                  title="Enlazar como adicional una cotización que ya se hizo en World Office"
                >
                  Enlazar COT
                </Boton>
              )}
              {!tienePrincipal && (
                <Boton
                  variante="secundario"
                  tamano="pequeno"
                  onClick={onEnlazarCotizacion}
                >
                  Enlazar COT
                </Boton>
              )}
              <Boton
                variante="secundario"
                tamano="pequeno"
                onClick={onEnlazarFactura}
              >
                Enlazar FEV
              </Boton>
            </>
          )}
          {/* EN EL MISMO RENGLÓN, A LA DERECHA. Johan señaló ese hueco:
              «¿no queda mejor ahí?». Iba solo en su renglón, centrado, debajo
              de los demás. */}
          {onVerDocumento && (
            // El morado lo lleva este: es lo que más se abre de la tarjeta.
            // «Armar adicional» va como los demás (Johan).
            <Boton
              variante="principal"
              tamano="pequeno"
              className={estilos.verDocumento}
              onClick={onVerDocumento}
              title="Ver el documento completo de la obra: todas sus cotizaciones"
            >
              Ver documento
            </Boton>
          )}
        </div>
      )}
    </div>
  );
}
